import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Phone,
  Settings,
  CheckCircle,
  XCircle,
  Loader2,
  Save,
  Trash2,
  TestTube,
  ArrowLeft,
  AlertTriangle,
  ExternalLink,
  Key,
  Hash,
  Activity,
  HelpCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientRingbaConfigPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [ringbaStatus, setRingbaStatus] = useState({
    global_enabled: false,
    client_enabled: false,
    is_configured: false
  });
  
  const [config, setConfig] = useState({
    account_id: '',
    api_token: '',
    campaign_id: '',
    target_id: '',
    number_pool_id: '',
    webhook_url: ''
  });
  
  const [existingConfig, setExistingConfig] = useState(null);
  const [testResult, setTestResult] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('client_token');
      const headers = { Authorization: `Bearer ${token}` };

      const res = await axios.get(`${API_URL}/api/client-portal/ringba/status`, { headers });
      setRingbaStatus(res.data);
      
      if (res.data.config) {
        setExistingConfig(res.data.config);
        setConfig({
          account_id: res.data.config.account_id || '',
          api_token: '', // Don't populate - it's encrypted
          campaign_id: res.data.config.campaign_id || '',
          target_id: res.data.config.target_id || '',
          number_pool_id: res.data.config.number_pool_id || '',
          webhook_url: res.data.config.webhook_url || ''
        });
      }
    } catch (error) {
      console.error('Error fetching Ringba status:', error);
      toast.error('Failed to load Ringba status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleSave = async () => {
    if (!config.account_id || !config.api_token) {
      toast.error('Account ID and API Token are required');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('client_token');
      
      await axios.put(
        `${API_URL}/api/client-portal/ringba/config`,
        config,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Ringba configuration saved successfully');
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      const token = localStorage.getItem('client_token');
      
      const res = await axios.post(
        `${API_URL}/api/client-portal/ringba/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTestResult(res.data);
      if (res.data.success) {
        toast.success('Connection test successful!');
      } else {
        toast.error(`Connection test failed: ${res.data.error}`);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Connection test failed';
      setTestResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete your Ringba configuration?')) {
      return;
    }

    try {
      setDeleting(true);
      const token = localStorage.getItem('client_token');
      
      await axios.delete(
        `${API_URL}/api/client-portal/ringba/config`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Ringba configuration deleted');
      setExistingConfig(null);
      setConfig({
        account_id: '',
        api_token: '',
        campaign_id: '',
        target_id: '',
        number_pool_id: '',
        webhook_url: ''
      });
      setTestResult(null);
    } catch (error) {
      toast.error('Failed to delete configuration');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  // Check if Ringba is available
  if (!ringbaStatus.global_enabled || !ringbaStatus.client_enabled) {
    return (
      <div className="p-6" data-testid="client-ringba-config-page">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/client-portal')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Phone className="w-6 h-6 text-orange-600" />
              Ringba Configuration
            </h1>
            <p className="text-muted-foreground">Configure your Ringba call tracking</p>
          </div>
        </div>
        
        <Card className="max-w-2xl">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Ringba Not Available</h3>
              <p className="text-muted-foreground mb-4">
                {!ringbaStatus.global_enabled 
                  ? 'Ringba integration is not enabled on this platform. Please contact support for more information.'
                  : 'Ringba has not been enabled for your account. Please contact your administrator.'}
              </p>
              <Button variant="outline" onClick={() => navigate('/client-portal')}>
                Back to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="client-ringba-config-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/client-portal')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Phone className="w-6 h-6 text-orange-600" />
              Ringba Configuration
            </h1>
            <p className="text-muted-foreground">Configure your Ringba call tracking integration</p>
          </div>
        </div>
        {existingConfig && (
          <Badge variant={existingConfig.is_active ? 'default' : 'secondary'}>
            {existingConfig.is_active ? 'Active' : 'Inactive'}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Configuration Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Required Credentials */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="w-5 h-5" />
                Ringba Credentials
              </CardTitle>
              <CardDescription>
                Enter your Ringba Account ID and API Token
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="account_id">Account ID *</Label>
                <Input
                  id="account_id"
                  placeholder="e.g., RA1234567890abcdef"
                  value={config.account_id}
                  onChange={(e) => setConfig(prev => ({ ...prev, account_id: e.target.value }))}
                  data-testid="ringba-account-id"
                />
                <p className="text-xs text-muted-foreground">
                  Find this in your Ringba dashboard under your profile icon
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="api_token">API Access Token *</Label>
                <Input
                  id="api_token"
                  type="password"
                  placeholder={existingConfig ? '••••••••••••••••' : 'Enter your API token'}
                  value={config.api_token}
                  onChange={(e) => setConfig(prev => ({ ...prev, api_token: e.target.value }))}
                  data-testid="ringba-api-token"
                />
                <p className="text-xs text-muted-foreground">
                  Generate in Ringba: Security → API Access Tokens → Add New
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Optional Configuration */}
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="optional" className="border rounded-lg">
              <AccordionTrigger className="px-4">
                <span className="flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Optional Settings
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="campaign_id">Default Campaign ID</Label>
                  <Input
                    id="campaign_id"
                    placeholder="e.g., CA1234567890"
                    value={config.campaign_id}
                    onChange={(e) => setConfig(prev => ({ ...prev, campaign_id: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Campaign to associate with leads from this platform
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="target_id">Default Target ID</Label>
                  <Input
                    id="target_id"
                    placeholder="e.g., TA1234567890"
                    value={config.target_id}
                    onChange={(e) => setConfig(prev => ({ ...prev, target_id: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Target (buyer) to route calls to
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="number_pool_id">Number Pool ID</Label>
                  <Input
                    id="number_pool_id"
                    placeholder="e.g., NP1234567890"
                    value={config.number_pool_id}
                    onChange={(e) => setConfig(prev => ({ ...prev, number_pool_id: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Number pool for dynamic number insertion
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="webhook_url">Webhook URL</Label>
                  <Input
                    id="webhook_url"
                    placeholder="https://your-domain.com/webhook/ringba"
                    value={config.webhook_url}
                    onChange={(e) => setConfig(prev => ({ ...prev, webhook_url: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    URL to receive Ringba event notifications
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* Test Result */}
          {testResult && (
            <Card className={testResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  {testResult.success ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Connection Successful</p>
                        <p className="text-sm text-green-600">{testResult.message}</p>
                      </div>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="font-medium text-red-800">Connection Failed</p>
                        <p className="text-sm text-red-600">{testResult.error}</p>
                      </div>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3">
            <Button onClick={handleSave} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Configuration
                </>
              )}
            </Button>
            
            {existingConfig && (
              <>
                <Button onClick={handleTest} disabled={testing} variant="outline">
                  {testing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Testing...
                    </>
                  ) : (
                    <>
                      <TestTube className="w-4 h-4 mr-2" />
                      Test Connection
                    </>
                  )}
                </Button>
                
                <Button onClick={handleDelete} disabled={deleting} variant="destructive">
                  {deleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Help Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <HelpCircle className="w-4 h-4" />
                Setup Guide
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <h4 className="font-medium mb-1">1. Get Account ID</h4>
                <p className="text-muted-foreground">
                  Click your profile icon in Ringba, then click the clipboard icon next to your company name.
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-1">2. Generate API Token</h4>
                <p className="text-muted-foreground">
                  Go to Security → API Access Tokens → Add API Access Token. Save the token securely.
                </p>
              </div>
              <div>
                <h4 className="font-medium mb-1">3. Test Connection</h4>
                <p className="text-muted-foreground">
                  After saving, click "Test Connection" to verify your credentials work.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => window.open('https://support.ringba.com', '_blank')}
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Ringba Documentation
              </Button>
            </CardContent>
          </Card>

          {existingConfig && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Activity className="w-4 h-4" />
                  Connection Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Configured</span>
                  <Badge variant="outline" className="bg-green-50 text-green-700">Yes</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Last Test</span>
                  <span>
                    {existingConfig.last_test_at 
                      ? new Date(existingConfig.last_test_at).toLocaleDateString()
                      : 'Never'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  {existingConfig.connection_tested ? (
                    existingConfig.last_test_result === 'success' ? (
                      <Badge className="bg-green-100 text-green-700">Connected</Badge>
                    ) : (
                      <Badge variant="destructive">Failed</Badge>
                    )
                  ) : (
                    <Badge variant="secondary">Not Tested</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
